import { z } from 'zod';

import { DIGEST_FREQUENCIES, NOTIFICATION_CATEGORIES } from './constants.js';

export const notificationCategorySchema = z.enum(NOTIFICATION_CATEGORIES);
export const digestFrequencySchema = z.enum(DIGEST_FREQUENCIES);

export const notificationPreferenceItem = z.object({
  category: notificationCategorySchema,
  channelEmail: z.boolean(),
  channelSms: z.boolean(),
  channelInApp: z.boolean(),
  digestFrequency: digestFrequencySchema.default('realtime'),
  urgentOverridesDigest: z.boolean().default(true),
});
export type NotificationPreferenceItem = z.infer<typeof notificationPreferenceItem>;

export const updateNotificationPreferencesInput = z.object({
  preferences: z.array(notificationPreferenceItem).min(1),
});
export type UpdateNotificationPreferencesInput = z.infer<typeof updateNotificationPreferencesInput>;

/** Default channel mix for a freshly-created user. Email + in-app on for
 *  every category; SMS off (each user opts in explicitly). */
export const DEFAULT_NOTIFICATION_PREFERENCES: NotificationPreferenceItem[] =
  NOTIFICATION_CATEGORIES.map((category) => ({
    category,
    channelEmail: true,
    channelSms: false,
    channelInApp: true,
    digestFrequency: 'realtime',
    urgentOverridesDigest: true,
  }));

export const quietHoursInput = z.object({
  enabled: z.boolean(),
  startLocalTime: z
    .string()
    .regex(/^\d{2}:\d{2}$/u, 'Use HH:mm format (24h)'),
  endLocalTime: z
    .string()
    .regex(/^\d{2}:\d{2}$/u, 'Use HH:mm format (24h)'),
  timezone: z.string().min(1).max(64).default('America/New_York'),
  urgentOverrides: z.boolean().default(true),
});
export type QuietHoursInput = z.infer<typeof quietHoursInput>;

export const phoneVerifyStartInput = z.object({
  phone: z
    .string()
    .regex(/^\+\d{10,15}$/u, 'Phone must be E.164 (e.g. +15551234567)'),
});
export type PhoneVerifyStartInput = z.infer<typeof phoneVerifyStartInput>;

export const phoneVerifyConfirmInput = z.object({
  code: z.string().regex(/^\d{6}$/u, 'Code must be 6 digits'),
});
export type PhoneVerifyConfirmInput = z.infer<typeof phoneVerifyConfirmInput>;

export const notificationListQuery = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20),
  unread: z
    .string()
    .optional()
    .transform((v) => v === 'true'),
  category: notificationCategorySchema.optional(),
});
